/**
 * reportApi — KY Report v2 백엔드 연동(목록/단건/저장/삭제) + 원본 raw 조립·재계산(buildReport).
 *
 * 학습(/students-hub) 전용. 구 ReportModal(공유 AI 리포트)에는 영향 없음.
 *   - 목록: GET    /api/v2/report?student_id=   → 저장된 리포트(생성 역순, meta만)
 *   - 단건: GET    /api/v2/report/{report_id}   → 저장 시점 report_data(원본 raw) 그대로
 *   - 저장: POST   /api/v2/report               → report_data = 당시 원본 raw blob + raw_schema 마커
 *   - 삭제: DELETE /api/v2/report/{report_id}    → soft-delete
 *
 * store-raw 모델(2026-07-06): 생성 시 원천 API를 조립한 '당시 원본 raw'(studentCard/examsPlanned/
 *   engine/genetics/conceptsMeta/ratingV2)를 report_data로 저장하고, 열람은 단건 GET으로 그 raw를
 *   받아 buildReport→어댑터로 프론트에서 재계산해 렌더한다. → 데이터는 저장 시점에 동결되고 산식은
 *   항상 최신이 적용된다(우선보강 산식 개정 소급). 레이팅은 생성 시 v2 응답을 저장하고 그대로 표시한다.
 *   과거 ratingExams 저장본도 읽되, 열람 시 현재 API 값으로 덮어쓰지 않는다.
 *   ratingV2에는 학생 식별 정보와 문제별 목록을 제외한 점수·이력만 저장한다.
 */
import { fetchAPI, fetchWithTokenRefresh } from '../../utils/apiUtils';
import { fetchMathConcepts } from '../../utils/mathConceptsUtils';
import { isEngineDefault } from '../../utils/aitrackerTimelineCache';
import { buildReport } from '../common/report/buildReport';
import { adaptReportForDraft } from './adaptReportForDraft';
import { fetchStudentRatingHistory } from '../../services/ratingApi';

// 우선순위 산식 버전 — store-raw 재계산은 항상 현재 산식을 쓰므로(산식 소급),
// 이력 추적용으로 meta에 저장한다. 산식·처방 풀 정의를 바꿀 때마다 이 값을 갱신할 것.
export const RX_FORMULA_VERSION = 'rx-2026-07-06';

// 저장 원본 raw payload 스키마 버전 — report_data에 심는 마커. buildReport가 소비하는 raw 계약
// 기본 raw 구조는 유지하고 ratingV2를 추가한다. 기존 ratingExams 저장본도 읽으므로 버전은 유지한다.
// 저장본을 읽을 수 없을 때는 현재 데이터로 대체하지 않는다(생성 시점 보존).
export const RAW_SCHEMA = 1;

// ── v2: 리포트 목록 ──
// 계약(ky-report-v2-api.md §4): data = [{report_id, report_uuid, created_at, meta:{select,formula_version}}]
// 실서버 응답이 계약과 다를 때(목록 중첩·meta JSON 문자열화·필드명 별칭) 화면이 통째로 비거나
// 선택 복원·산식 배너가 오동작하므로, 관측된 편차 유형에 방어적으로 정규화한다(2026-07-04 GET 경로 점검).
function normalizeReportEntry(raw) {
  if (!raw || typeof raw !== 'object') return null;
  let meta = raw.meta;
  if (typeof meta === 'string') {
    try { meta = JSON.parse(meta); } catch (_) { meta = {}; }
  }
  const entry = {
    ...raw,
    report_id: raw.report_id ?? raw.id ?? null,
    report_uuid: raw.report_uuid ?? raw.uuid ?? null,
    created_at: raw.created_at ?? raw.createdAt ?? null,
    meta: meta && typeof meta === 'object' ? meta : {},
  };
  return entry.report_id == null ? null : entry;
}

export async function fetchReportList(studentId, getAccessToken) {
  const res = await fetchWithTokenRefresh(`/api/v2/report?student_id=${studentId}`, {}, getAccessToken);
  const d = res?.data;
  const nested = Array.isArray(d?.reports) ? d.reports
    : Array.isArray(d?.list) ? d.list
      : Array.isArray(d?.items) ? d.items
        : null;
  const arr = Array.isArray(d) ? d : (nested || []);
  if (!Array.isArray(d) && nested == null && d != null) {
    // 계약 밖 응답 형태 — 목록이 비어 보이는 원인 추적용(배열·알려진 중첩 형태에는 발동하지 않음)
    console.warn('[reportApi] 리포트 목록 응답이 계약 형태(배열)가 아닙니다:', d);
  }
  const list = arr.map(normalizeReportEntry).filter(Boolean);
  // 계약상 생성 역순이지만 방어 정렬(정렬 미보장 서버 대응)
  list.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
  return list;
}

// ── v2: 리포트 단건 조회 ──
// 저장 시점 report_data(원본 raw)를 반환. data = { report_id, report_uuid, created_at, meta, report_data }.
// 404/네트워크 실패는 throw(apiUtils) → 상위(HubReportModal)가 오류로 표시한다.
// 방어 파싱: 백엔드가 JSONB를 TEXT로 문자열화해 내리면(목록 meta에서 실관측 — normalizeReportEntry) report_data/meta가
// 문자열로 와 raw_schema 판별이 실패→store-raw 재계산이 조용히 무력화된다. 목록 경로와 동일하게 문자열이면 파싱한다.
export async function fetchReport(reportId, getAccessToken) {
  const res = await fetchWithTokenRefresh(`/api/v2/report/${reportId}`, {}, getAccessToken);
  const data = res?.data ?? null;
  if (!data || typeof data !== 'object') return data;
  const parseMaybe = (v) => {
    if (typeof v !== 'string') return v;
    try { return JSON.parse(v); } catch (_) { return v; }
  };
  return { ...data, report_data: parseMaybe(data.report_data), meta: parseMaybe(data.meta) };
}

// concept_type 추출 — AITrackerContent.extractConceptType와 동일 규칙
function extractConceptType(concept) {
  if (concept?.concept_type) return concept.concept_type;
  if (concept?.text && concept.text.includes(':')) {
    const prefix = concept.text.split(':')[0];
    if (prefix.includes('-')) return prefix.split('-')[1];
  }
  return null;
}

// ── 리포트 생성 선택기용 개념 표 ──
// /genetics/engine(직접·연관·안정도) + /mathcepts/temp(메타)를 합쳐 AITracker 수학개념 표와
// 동일한 행 모델을 만든다(값·정렬·태그·타입 동일). x=직접(r), y=연관(h), z=안정도(st).
//   engine.concept_index[i] ↔ r_status/h_status/stability[i] (v_scale로 정규화)
//   meta(mathcepts/temp): order, math_type, concept_type, tags, text(개념명)
// isDefault = 엔진 초기값(학습 기록 미반영) 여부 — AITracker '초기값 숨김'과 동일 판정(isEngineDefault).
//   여기서 걸러내지 않고 플래그만 실어 보낸다. 숨김 여부는 데이터를 쓰는
//   HubConceptPickerTable의 토글과 호출부 정책이 결정한다.
export async function fetchConceptRows(studentId, getAccessToken) {
  const [engineRes, mathConcepts] = await Promise.all([
    fetchWithTokenRefresh(`/api/genetics/engine?student_id=${studentId}`, {}, getAccessToken),
    fetchMathConcepts(),
  ]);
  const engine = engineRes?.data;
  const idx = engine?.concept_index;
  if (!Array.isArray(idx) || idx.length === 0) return [];

  const vScale = engine.v_scale || 10000;
  const rArr = engine.r_status || [];
  const hArr = engine.h_status || [];
  const sArr = engine.stability || [];
  const decodeQ = (q) => (q === null || q === undefined ? null : q / vScale);

  const map = new Map();
  (mathConcepts || []).forEach((c) => map.set(String(c.id), c));

  const rows = [];
  for (let i = 0; i < idx.length; i += 1) {
    const cid = idx[i];
    const x = decodeQ(rArr[i]);
    const y = decodeQ(hArr[i]);
    if (x === null || y === null) continue; // AITracker와 동일 필터
    const concept = map.get(String(cid));
    const raw = concept?.text || `개념 ${cid}`;
    const rawName = raw.includes(':') ? raw.split(':').slice(1).join(':').trim() : raw;
    rows.push({
      cid,
      x,
      y,
      z: decodeQ(sArr[i]) ?? 0,
      isDefault: isEngineDefault(rArr[i], hArr[i], sArr[i], vScale), // 양자화 원본값 기준(표시값 비교 금지)
      order: concept?.order ?? null,
      mathType: concept?.math_type || null,
      conceptType: extractConceptType(concept),
      rawName,
      tags: Array.isArray(concept?.tags) ? concept.tags : [],
    });
  }
  return rows;
}

// ── v2: 리포트 삭제(soft-delete) ──
export async function deleteReport(reportId, getAccessToken) {
  const res = await fetchWithTokenRefresh(`/api/v2/report/${reportId}`, { method: 'DELETE' }, getAccessToken);
  return res?.data ?? null;
}

// ── v2: 리포트 미리보기 생성(미저장) ──
// select 비우면 전체(둘 다 빈 배열). 반환 = report_data(원시/최소 형태).
export async function generateReport(studentId, select, getAccessToken) {
  const res = await fetchWithTokenRefresh('/api/v2/report/generate', {
    method: 'POST',
    body: JSON.stringify({ student_id: studentId, select: select || { concept_ids: [], problem_ids: [] } }),
  }, getAccessToken);
  return res?.data?.report_data ?? null;
}

// ── v2: 리포트 저장 ──
export async function saveReport(studentId, reportData, meta, getAccessToken) {
  const res = await fetchWithTokenRefresh('/api/v2/report', {
    method: 'POST',
    body: JSON.stringify({ student_id: studentId, report_data: reportData, meta: meta || {} }),
  }, getAccessToken);
  const data = res?.data ?? null; // { report_id|id, report_uuid|uuid }
  // GET 정규화(normalizeReportEntry)와 동일하게 id 별칭을 수용 — POST만 엄격하면 백엔드 필드 드리프트 시
  // 저장은 됐는데 throw→재시도로 중복 저장된다. 진짜 id 부재만 표면화(계획서 §3.4).
  const reportId = data ? (data.report_id ?? data.id) : null;
  if (reportId == null) throw new Error('리포트 저장 응답이 올바르지 않습니다(report_id 없음).');
  return { ...data, report_id: reportId };
}

// 리포트 생성+저장(store-raw): generate(학습결과 게이트) → fetchStudentReportRaw(당시 원본 raw) → report(raw 저장).
// conceptIds 비우면 전체.
// 가드: /generate 응답에 학습 결과(푼 문제)가 없으면 저장하지 않고 생성을 취소한다.
//   '결과 없음' 신호 = problems 공집합(= problem_count 0). v2 /generate 응답(ky-report-v2-api.md §2)은
//   r/h/st(직접·연관·안정도 = 유전자 엔진 숙련도)를 담지 않으며, 숙련도 0은 '오답(약한 개념)'일 뿐
//   결과 있음이므로 취소 신호가 아니다. accuracy(null=미채점)·concepts.length(선택개념 0건 에코 가능)도 쓰지 않는다.
export async function createStudentReport(studentId, conceptIds, getAccessToken) {
  const ids = Array.isArray(conceptIds) ? conceptIds : [];
  const select = { concept_ids: ids, problem_ids: [] };
  // 1) 학습 결과 유무 게이트 — /generate의 선택 스코프 problem_count가 authoritative(무회귀 유지).
  const preview = await generateReport(studentId, select, getAccessToken);
  const problemCount = preview?.problem_count ?? preview?.problems?.length ?? 0;
  if (!preview || problemCount === 0) {
    throw new Error(
      ids.length === 0
        ? '학생의 학습 결과가 없어 리포트를 생성할 수 없습니다.'
        : '선택한 개념에 해당하는 학습 결과가 없어 리포트를 생성할 수 없습니다.',
    );
  }
  // 2) 저장물 = '당시 원본 raw'(계산 결과 아님). 열람 시 이 raw로 buildReport 재계산하므로
  //    데이터는 저장 시점에 동결되고 산식은 항상 최신이 적용된다(우선보강 산식 개정 소급).
  const raw = await fetchStudentReportRaw(studentId, getAccessToken);
  // 벨트: /generate는 통과했지만 유전자 stamps 공집합 등으로 재계산이 퇴화하면 저장하지 않는다(빈/깨진 저장 방지).
  const view = buildStudentReportView(raw, ids);
  if (!view?.meta?.counts?.attemptCount) {
    throw new Error('학습 이력이 없어 리포트를 저장할 수 없습니다.');
  }
  // 3) 선택 리포트인데 처방 대상(세이프가드 풀 매칭)이 0개면 보강할 공식이 없어 생성을 차단한다.
  //    matched = 선택 개념 ∩ 처방 풀(adaptReportForDraft.rxSelection). 학습 결과는 있으나(1단계 통과)
  //    선택 개념이 전부 숙달·비취약이라 Top5를 전체 풀로 채우게 되는 경우 — 사용자 요청으로 차단
  //    (2026-07-04 '매칭 0도 전체 채움' 방침 재정정: 선택 리포트는 보강 대상이 있어야 생성).
  if (ids.length > 0 && view.rxSelection && view.rxSelection.matched === 0) {
    throw new Error('선택한 매스컨셉 중 보강이 필요한 항목이 없습니다(모두 숙달했거나 진단 대상이 아님). 다른 매스컨셉을 선택해 주세요.');
  }
  // JSON 왕복으로 NaN→null·undefined 키를 확정(저장 raw = 재계산 입력본 보장).
  const payload = JSON.parse(JSON.stringify({ ...raw, raw_schema: RAW_SCHEMA }));
  return saveReport(
    studentId,
    payload,
    { select: { concept_ids: ids }, formula_version: RX_FORMULA_VERSION, raw_schema: RAW_SCHEMA },
    getAccessToken,
  );
}

// 개념 메타 수집: 서버가 큰 배치에서 500을 내므로 청크 50 + 실패 시 이분 분할로 복원
// (scripts/fetch-student-report-data.mjs와 동일 전략)
async function fetchConceptMeta(conceptIds, getAccessToken) {
  const merged = [];
  const seen = new Set();
  async function fetchIds(ids) {
    if (!ids.length) return;
    let res;
    try {
      res = await fetchWithTokenRefresh(
        `/api/mathcepts/temp/by-concept-ids?concept_ids=${ids.join(',')}`, {}, getAccessToken,
      );
    } catch (e) {
      if (ids.length === 1) return; // 단일 id 실패 → 건너뜀
      const mid = Math.floor(ids.length / 2);
      await fetchIds(ids.slice(0, mid));
      await fetchIds(ids.slice(mid));
      return;
    }
    const arr = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
    arr.forEach((c) => {
      if (c?.concept_id != null && !seen.has(c.concept_id)) { seen.add(c.concept_id); merged.push(c); }
    });
  }
  const CHUNK = 50;
  for (let i = 0; i < conceptIds.length; i += CHUNK) {
    await fetchIds(conceptIds.slice(i, i + CHUNK));
  }
  return merged;
}

// ── genetics 전용 fetch: 404 를 「기록 없음」으로 갈라 읽는다 ──
// 공용 fetchWithTokenRefresh 는 상태코드를 버리고 404 를 「요청하신 데이터를 찾을 수 없습니다」라는
// 고정 문구로 갈아 끼운다(utils/apiUtils.js). 그 문구는 서버 장애와 기록 없음을 구별해 주지 못하는데,
// 이 엔드포인트의 404 에는 계약이 붙어 있다 — **스탬프 없음 AND 마감된 신규 시험지 없음** 일 때만
// 404 다(BE 회신 2026-08-29 §1 2-3, docs/exam-builder-v2/BE-20260829-reply-fe-0828.md).
// 즉 404 = 「학습 기록이 아예 없다」이고, 그 밖의 실패는 장애다. 화면이 둘을 다른 말로 해야 해서
// 이 한 호출만 상태코드를 살려 읽는다(형제 구현: ProblemHubV2/problemApi.js 의 getJsonWithStatus).
// 성공 경로의 반환값은 fetchWithTokenRefresh 와 같다(응답 JSON 그대로) — 호출부를 바꾸지 않기 위해서다.
async function fetchGenetics(studentId, getAccessToken) {
  const token = typeof getAccessToken === 'function' ? await getAccessToken() : null;
  if (!token) { window.location.href = '/signin'; return null; } // 공용 유틸과 같은 모양(리다이렉트 + null)
  let response;
  try {
    response = await fetchAPI(`/api/genetics/students?student_id=${studentId}`, {
      method: 'GET',
      headers: { Authorization: token, 'Content-Type': 'application/json' },
    });
  } catch (netErr) {
    throw new Error('서버에 연결할 수 없습니다. 인터넷 연결을 확인하거나 잠시 후 다시 시도해주세요.');
  }
  if (response.status === 401) {
    // 공용 유틸과 같은 뒷정리 — 죽은 토큰을 남겨 두면 다음 화면이 또 401 을 맞는다.
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    window.location.href = '/signin';
    return null;
  }
  if (response.status === 404) {
    // 계약이 정한 유일한 404 조건을 그대로 말한다. 「찾을 수 없습니다」로 끝내면 사람은 장애로 읽고
    // 다시 눌러 보거나 신고한다 — 다시 눌러도 결과는 같다.
    const err = new Error('이 학생은 이수한 스탬프도, 채점 완료된 시험지도 아직 없어 리포트를 만들 수 없습니다.');
    err.httpStatus = 404;
    throw err;
  }
  if (!response.ok) {
    // 404 밖의 상태는 여전히 「장애」다. 공용 유틸이 주던 안내의 결을 유지하되(apiUtils 의 상태별 문구),
    // 무엇이 실패했는지를 앞에 붙인다 — 조각 이름이 없으면 다섯 호출 중 어느 것이 죽었는지 알 수 없다.
    const tail = response.status === 403 ? '접근 권한이 없습니다.'
      : response.status >= 500 ? '서버에 문제가 발생했습니다. 잠시 후 다시 시도해 주세요.'
        : `(${response.status})`;
    const err = new Error(`학습 개념 데이터를 불러오지 못했습니다 — ${tail}`);
    err.httpStatus = response.status;
    throw err;
  }
  try {
    return await response.json();
  } catch (_) {
    throw new Error('학습 개념 데이터의 서버 응답을 읽을 수 없습니다.');
  }
}

// ── 원본 raw 수집: 저장 스냅샷의 원천이자 buildReport 입력. 원천 API를 조립해 '당시 raw' blob 반환.
// 이 blob을 report_data로 저장하고, 열람 시 buildStudentReportView로 재계산한다(store-raw). ──
export async function fetchStudentReportRaw(studentId, getAccessToken) {
  // 리포트는 아래 조각들을 한 번에 모아야 만들어진다. 하나가 죽으면 Promise.all 이 통째로 넘어지는데,
  // 공용 유틸의 상태코드별 고정 문구는 **무엇이** 없는지를 말하지 않는다. 그래서 실패한 조각의 이름을
  // 앞에 붙여 다시 던진다 — 삼키지 않는다(조각 하나만 비어도 리포트는 사실과 다른 그림이 된다).
  const named = (label, p) => p.catch((e) => {
    throw new Error(`${label} 불러오지 못했습니다 — ${e?.message || '알 수 없는 오류'}`);
  });
  // genetics 는 합성 그룹(stamp_id:null — 스탬프 없이 신규 시험지만 마감된 학생)을 함께 줄 수 있다.
  // buildReport 는 stamp_id 를 flatAttempts 꼬리표로만 쓰므로 그대로 흘려보내면 된다(BE 회신 §1 2-3).
  const [card, exams, genetics, engine, ratingV2] = await Promise.all([
    named('학생 정보를', fetchWithTokenRefresh(`/api/users/student/card?student_id=${studentId}`, {}, getAccessToken)),
    named('시험(스탬프) 목록을', fetchWithTokenRefresh(`/api/exams?student_id=${studentId}`, {}, getAccessToken)),
    fetchGenetics(studentId, getAccessToken), // 404 문구를 스스로 만든다 — named 로 감싸면 그 말이 묻힌다
    named('학습 엔진 데이터를', fetchWithTokenRefresh(`/api/genetics/engine?student_id=${studentId}`, {}, getAccessToken)),
    named('레이팅 정보를', fetchStudentRatingHistory(studentId, getAccessToken)),
  ]);

  if (ratingV2 == null) throw new Error('레이팅 정보를 불러오지 못했습니다. 로그인 상태를 확인해주세요.');

  const geneticsData = genetics?.data;
  const conceptIds = Object.keys(geneticsData?.concepts || {}).map(Number);
  if (!conceptIds.length) {
    throw new Error('학습 개념 데이터가 없어 리포트를 생성할 수 없습니다.');
  }
  const conceptsMeta = await fetchConceptMeta(conceptIds, getAccessToken);

  return {
    studentCard: card?.data,
    examsPlanned: exams?.data?.stamps || [],
    engine: engine?.data,
    genetics: geneticsData,
    conceptsMeta,
    ratingV2,
  };
}

// ── 순수 계산: raw → buildReport → 뷰모델(어댑터). fetch 없음(저장 raw 재계산·라이브 재계산 공용).
// selectedConceptIds: 처방(P5)을 이 개념들로 스코프(비우면 전체). ──
export function buildStudentReportView(raw, selectedConceptIds = []) {
  const d = buildReport({
    studentCard: raw?.studentCard,
    examsPlanned: raw?.examsPlanned || [],
    engine: raw?.engine,
    genetics: raw?.genetics,
    conceptsMeta: raw?.conceptsMeta,
  });
  // buildReport 출력 → DnaReportDraft 뷰모델로 변환(파생 필드 채움) + 처방(P5) 선택 스코프 + 폼레이팅
  return adaptReportForDraft(d, selectedConceptIds, raw?.ratingExams || [], raw?.ratingV2);
}

// ── 미저장 미리보기: 현재 데이터로 raw 조립 → 뷰모델. 저장 리포트에는 적용하지 않는다. ──
export async function buildStudentReportData(studentId, getAccessToken, selectedConceptIds = []) {
  const raw = await fetchStudentReportRaw(studentId, getAccessToken);
  return buildStudentReportView(raw, selectedConceptIds);
}
